// Deployment identity — the ONE portal module that knows which deployment this
// bundle is serving: the Auth0 custom-claim namespace, the staff email
// domain(s), and the support address.
//
// The same `main` (and, per environment, the same image) serves more than one
// organization, so none of these may be source code. They arrive the way the
// rest of the per-environment config does (see ./runtimeConfig.ts):
//
//   1. RUNTIME — the container entrypoint (docker-entrypoint.sh) writes
//      /config.js from the container's env vars, which sets
//      `window.__APP_CONFIG__` before the bundle runs. This is what lets one
//      image promote dev → qa → prod.
//   2. BUILD — a `VITE_*` value baked into the bundle (local `vite dev` reading
//      `.env`, or a build-arg).
//   3. TRANSITIONAL DEFAULT — the literal the call site carried before this
//      module existed. Those literals live here and nowhere else in the portal;
//      `__tests__/deployment-identity-guard.test.ts` fails the suite when one
//      appears in any other non-test source file.
//
// A blank value at one tier falls through to the next, so an environment that
// injects nothing (every LDS environment today — neither the portal manifest
// nor CI sets any of these) is unchanged.

/**
 * Today's literals, verbatim. None of the three is set by the LDS portal
 * manifest (`clouddeploy/manifests/portal.yaml`) or baked by CI — LDS dev and
 * qa both run on these defaults.
 */
export const TRANSITIONAL_DEFAULTS = {
  /** Prefix of the custom claims the tenant's Post-Login Action stamps (`<namespace>/roles`, ...). Must equal the API's AUTH0_CLAIM_NAMESPACE. */
  auth0ClaimNamespace: 'https://legaldocumentserver.com',
  /** Email domains allowed to self-register as platform staff at /admin-register. */
  staffEmailDomains: ['legaldocumentserver.com'],
  /** Customer-facing support address. */
  supportEmail: 'support@legaldocumentserver.com',
} as const;

/** The identity keys of `window.__APP_CONFIG__` (written by docker-entrypoint.sh). */
export interface DeploymentRuntimeInput {
  /** Container env `AUTH0_CLAIM_NAMESPACE`. */
  auth0ClaimNamespace?: string;
  /** Container env `STAFF_EMAIL_DOMAINS` — comma-separated. */
  staffEmailDomains?: string;
  /** Container env `SUPPORT_EMAIL`. */
  supportEmail?: string;
}

/** The identity keys of `import.meta.env`. */
export interface DeploymentBuildInput {
  VITE_AUTH0_CLAIM_NAMESPACE?: string;
  VITE_STAFF_EMAIL_DOMAINS?: string;
  VITE_SUPPORT_EMAIL?: string;
}

export interface DeploymentConfig {
  /** Claim namespace, without a trailing slash. */
  auth0ClaimNamespace: string;
  /** Lower-cased staff email domains; never empty. */
  staffEmailDomains: readonly string[];
  supportEmail: string;
}

/** First non-blank of the runtime value, then the build value; trimmed. */
function configured(runtimeValue: string | undefined, buildValue: string | undefined): string | undefined {
  for (const candidate of [runtimeValue, buildValue]) {
    const trimmed = typeof candidate === 'string' ? candidate.trim() : '';
    if (trimmed.length > 0) return trimmed;
  }
  return undefined;
}

/** Comma-separated domains → lower-cased entries, a leading `@` tolerated. */
function parseDomains(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim().replace(/^@/, '').toLowerCase())
    .filter(Boolean);
}

/**
 * Pure resolver — runtime tier, then build tier, then the transitional default.
 * Exported so the tiers can be unit-tested without touching `window`.
 */
export function resolveDeploymentConfig(
  runtime: DeploymentRuntimeInput = {},
  build: DeploymentBuildInput = {},
): DeploymentConfig {
  const namespace =
    configured(runtime.auth0ClaimNamespace, build.VITE_AUTH0_CLAIM_NAMESPACE) ??
    TRANSITIONAL_DEFAULTS.auth0ClaimNamespace;
  const domains = parseDomains(configured(runtime.staffEmailDomains, build.VITE_STAFF_EMAIL_DOMAINS) ?? '');
  return {
    auth0ClaimNamespace: namespace.replace(/\/+$/, ''),
    // A value that parses to nothing (e.g. ",") is no configuration at all.
    staffEmailDomains: domains.length > 0 ? domains : [...TRANSITIONAL_DEFAULTS.staffEmailDomains],
    supportEmail: configured(runtime.supportEmail, build.VITE_SUPPORT_EMAIL) ?? TRANSITIONAL_DEFAULTS.supportEmail,
  };
}

/** This bundle's deployment identity, resolved once at module load. */
export const deploymentConfig: DeploymentConfig = resolveDeploymentConfig(
  (typeof window !== 'undefined' && window.__APP_CONFIG__) || {},
  import.meta.env,
);

/** The custom claims the Post-Login Action stamps under the namespace. */
export type Auth0ClaimName = 'roles' | 'org_id' | 'org_name' | 'approved' | 'platform_role' | 'app_metadata';

/** The token property a custom claim lives under: `${namespace}/${name}`. */
export function auth0ClaimKey(name: Auth0ClaimName, config: DeploymentConfig = deploymentConfig): string {
  return `${config.auth0ClaimNamespace}/${name}`;
}
